/**
 * ScenarioBrief — le brief de session en 4 sections, partagé entre l'écran d'entraînement
 * et l'historique (30/07/2026).
 *
 * Avant, l'onglet « Scénario » affichait un long texte unique qui répétait l'offre, la
 * fonction, l'entreprise, la taille et le secteur déjà présents ailleurs. Chaque donnée
 * n'a désormais qu'un seul endroit.
 *
 * Les DEUX pages rendent ce composant, mais leurs sources diffèrent — c'est volontaire :
 *  - Entraînement : le cas de simulation en cours (données vivantes) ;
 *  - Historique   : le scénario FIGÉ + son instantané `variables`. Relire le cas serait un
 *    piège : le supprimer est un vrai DELETE, ce qui viderait rétroactivement des sessions
 *    passées, et le modifier réécrirait l'histoire.
 * Chaque page fournit donc les champs déjà résolus/localisés ; ce composant ne fait que
 * présenter.
 */
import type { ReactNode } from "react";

/** Rendu markdown SÛR (arbre React, pas de dangerouslySetInnerHTML).
 *  Repris de HistoryPage : c'était la meilleure des deux implémentations qui coexistaient
 *  — l'autre (TrainingPage) injectait du HTML et ignorait les titres `###`, si bien qu'un
 *  même texte ne s'affichait pas pareil selon l'écran. */
export function renderBriefMarkdown(md: string) {
  const lines = md.split("\n");
  const elements: ReactNode[] = [];
  let listItems: string[] = [];
  let listOrdered = false;

  const renderInline = (text: string): ReactNode => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} className="text-foreground font-medium">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const flushList = () => {
    if (listItems.length > 0) {
      const Tag = listOrdered ? "ol" : "ul";
      elements.push(
        <Tag key={elements.length} className={`text-sm text-muted-foreground ml-4 space-y-1 ${listOrdered ? "list-decimal" : "list-disc"}`}>
          {listItems.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
        </Tag>,
      );
      listItems = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "") { flushList(); continue; }
    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(<h4 key={elements.length} className="font-medium text-sm mt-4 mb-1">{trimmed.slice(4)}</h4>);
    } else if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(<h3 key={elements.length} className="font-semibold text-base mt-4 mb-2">{trimmed.slice(3)}</h3>);
    } else if (trimmed.startsWith("- ")) {
      listOrdered = false;
      listItems.push(trimmed.slice(2));
    } else if (/^\d+\.\s/.test(trimmed)) {
      listOrdered = true;
      listItems.push(trimmed.replace(/^\d+\.\s/, ""));
    } else {
      flushList();
      elements.push(<p key={elements.length} className="text-sm text-muted-foreground">{renderInline(trimmed)}</p>);
    }
  }
  flushList();
  return <div className="space-y-1">{elements}</div>;
}

/** Encadré d'une section. Les 4 partagent le même cadre : elles sont de même rang. */
export function ScenarioSection({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
        <span>{icon}</span>{title}
      </p>
      {children}
    </div>
  );
}

export type ScenarioBriefLabels = {
  yourOffer: string;
  /** Même section, en SERVICE CLIENT : l'offre n'est pas ce qu'on vend mais ce que
   *  l'interlocuteur possède déjà, ex. « Ce que le client a acheté ». Volontairement sans
   *  variante au pluriel : la phrase vaut pour une offre comme pour plusieurs. */
  yourOfferCare: string;
  offers: string;
  yourInterlocutor: string;
  /** Préfixe de la ligne de style, ex. « Style ». */
  personalityStyle: string;
  prospectCompany: string;
  knownContext: string;
  /** Titre du repli : scénario généré avant ce chantier → texte intégral. */
  context: string;
  /** Titre du bloc propre au simulateur joué, ex. « Ce qu'il faut savoir ». */
  simulatorBrief: string;
  /** Titre du bloc du niveau joué, ex. « Niveau de difficulté ». */
  difficulty: string;
  /** Les trois niveaux, dans la langue d'entraînement. BizCoach les traduit lui-même :
   *  contrairement au style de personnalité, ils ne viennent pas de N8N mais de notre enum. */
  difficultyEasy: string;
  difficultyMedium: string;
  difficultyHard: string;
  /** Titre du bloc des critères sur-pondérés, ex. « Ce cas insiste sur ». */
  focusCriteria: string;
};

export type ScenarioBriefProps = {
  /** Nom du prospect, généré par N8N. */
  prospectName?: string | null;
  /** Nom de l'entreprise du prospect, générée par N8N. */
  companyName?: string | null;
  /** Description de l'entreprise du prospect (N8N). Vide tant que le workflow n'est pas migré. */
  companyDescription?: string | null;
  /** Contexte connu avant l'appel : brief joueur BizCoach, affiché tel quel. */
  priorContext?: string | null;
  /** Récit complet du scénario. Sert de DERNIER repli pour la section « contexte » quand
   *  ni le cas ni N8N n'ont fourni de champ dédié. */
  legacyFullText?: string | null;
  /** Ce que le simulateur joué ajoute de PROPRE à son exercice (markdown, N8N).
   *
   *  Chaque gabarit de brief N8N écrit des informations que les autres n'ont pas : Negociator
   *  les éléments de la proposition déjà partagée et les points encore ouverts, ArguWin les cinq
   *  besoins identifiés, MEDDIC la maturité du deal, Pitch le temps de parole, Satisfactor le
   *  motif de l'appel. Rien de cela n'atteignait l'écran — on n'affiche que les champs
   *  structurés, et le texte complet ne sert plus que de repli. Le plus gênant était
   *  Negociator : négocier sans savoir ce qui a été proposé.
   *
   *  Vide pour un simulateur sans spécificité (Prospector, Discovery) : la section est masquée
   *  plutôt que d'afficher un encadré creux. */
  simulatorBrief?: string | null;

  /** Simulateur de SERVICE CLIENT (`simulators.category === "customer_care"`).
   *
   *  Masque « L'entreprise du prospect ». En service client l'interlocuteur est DÉJÀ client :
   *  le wizard ne demande ni taille ni secteur (ils n'ont pas de sens ici), le cas n'en porte
   *  donc pas, et N8N n'en renvoie aucun — vérifié sur 49 scénarios Satisfactor : zéro secteur,
   *  une description sur 49, et un nom d'entreprise qui vaut toujours « Entreprise », le repli
   *  de `training.ts`. La section affichait donc un titre qui promet et une ligne qui n'apprend
   *  rien, ce qui est pire que pas de section.
   *
   *  La CATÉGORIE et pas le nom du simulateur : `simulators.name` est éditable en admin et
   *  diverge entre environnements, la catégorie est la donnée stable — et c'est déjà elle que
   *  le wizard lit pour masquer les champs, donc les deux écrans disent la même chose. */
  isCustomerCare?: boolean;
  /** Niveau joué : `easy` | `medium` | `hard` (`scenario_requests.difficulty`).
   *
   *  Vient de BizCoach, pas de N8N — l'apprenant l'a choisi, ou un parcours / challenge /
   *  tournoi le lui a imposé. Affiché surtout pour l'HISTORIQUE : trois semaines plus tard,
   *  personne ne se souvient du niveau joué, et c'est la seule trace lisible.
   *
   *  Une valeur inconnue n'affiche rien plutôt qu'une clé technique. */
  difficulty?: string | null;

  /** Libellé de l'offre (déjà résolu : titre personnalisé ou nom marketing). */
  offerLabel?: string;
  offerDescription?: string;
  /** Nombre d'offres, pour le pluriel du titre. */
  offerCount?: number;

  /** Sous-titre de l'interlocuteur : fonction en B2B, CSP · âge en B2C. Déjà localisé. */
  interlocutorSubtitle?: string;
  /** Style de personnalité joué pendant CETTE session (déjà localisé). Il conditionne le
   *  comportement de l'agent : l'apprenant a intérêt à le connaître avant de décrocher. */
  personalityStyle?: string;
  /** « secteur · taille », déjà localisé. Vide = ligne masquée. */
  companyMeta?: string;
  /** B2C : un particulier n'a pas d'entreprise ⇒ section 3 masquée. */
  isB2C?: boolean;
  /** Les deux ou trois critères que CE cas sur-pondère, déjà localisés.
   *  Ils existaient en base et partaient au scoring sans jamais être montrés à celui qui allait
   *  être noté dessus. Les afficher ici, avec les mots exacts du débrief, est ce qui permet de
   *  comprendre son score au lieu de le subir. Vide = bloc masqué : un cas sans sur-pondération
   *  ne doit pas afficher une promesse creuse. */
  focusCriteria?: string[];

  labels: ScenarioBriefLabels;
};

export function ScenarioBrief(props: ScenarioBriefProps) {
  const {
    prospectName, companyName, companyDescription, priorContext, legacyFullText, simulatorBrief,
    offerLabel, offerDescription, offerCount = 0, interlocutorSubtitle, personalityStyle,
    companyMeta, isB2C, isCustomerCare, labels, focusCriteria = [], difficulty,
  } = props;

  // Clé technique → libellé dans la langue d'entraînement. Une valeur hors enum (donnée
  // ancienne, écriture directe en base) ne doit jamais s'afficher telle quelle à l'écran.
  const niveauJoue = difficulty === "easy" ? labels.difficultyEasy
    : difficulty === "medium" ? labels.difficultyMedium
    : difficulty === "hard" ? labels.difficultyHard
    : null;

  return (
    <>
      {/* 1. L'offre. En service client, elle change de SENS : l'interlocuteur est déjà
          client, ce bloc dit ce qu'il POSSÈDE et donc pourquoi il appelle — pas ce que le
          commercial a à vendre. D'où un titre à lui (décision Mariam du 26/10/2026), choisi
          sur la CATÉGORIE du simulateur et jamais sur son nom.
          Pas de variante au pluriel en service client : « Ce que le client a acheté » vaut
          pour une offre comme pour plusieurs. */}
      {offerLabel ? (
        <ScenarioSection
          icon="💼"
          title={isCustomerCare ? labels.yourOfferCare : offerCount > 1 ? labels.offers : labels.yourOffer}
        >
          <p className="text-sm font-medium text-slate-800">{offerLabel}</p>
          {offerDescription ? (
            <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line break-words">{offerDescription}</p>
          ) : null}
        </ScenarioSection>
      ) : null}

      {/* 2. Ton interlocuteur — la PERSONNE (nom + fonction). Le secteur et la taille
          décrivent son entreprise : ils sont en section 3. */}
      {prospectName || interlocutorSubtitle || personalityStyle ? (
        <ScenarioSection icon="🎯" title={labels.yourInterlocutor}>
          {prospectName ? <p className="text-sm font-medium text-slate-800">{prospectName}</p> : null}
          {interlocutorSubtitle ? <p className="text-xs text-muted-foreground mt-0.5">{interlocutorSubtitle}</p> : null}
          {/* Le style pilote le comportement de l'agent pendant l'appel : le savoir avant
              de décrocher fait partie du brief. Mis en avant plutôt que noyé dans le
              sous-titre, car c'est une consigne de jeu, pas une donnée d'identité. */}
          {personalityStyle ? (
            <p className="text-xs text-slate-600 mt-1">
              <span className="font-medium">{labels.personalityStyle}</span> : {personalityStyle}
            </p>
          ) : null}
        </ScenarioSection>
      ) : null}

      {/* 3. L'entreprise du prospect — masquée en B2C ET en service client (voir la prop). */}
      {!isB2C && !isCustomerCare && (companyName || companyMeta || companyDescription) ? (
        <ScenarioSection icon="🏢" title={labels.prospectCompany}>
          {companyName ? <p className="text-sm font-medium text-slate-800">{companyName}</p> : null}
          {companyMeta ? <p className="text-xs text-muted-foreground mt-0.5">{companyMeta}</p> : null}
          {/* Texte simple : le contrat N8N interdit le markdown sur ce champ. */}
          {companyDescription ? (
            <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line break-words">{companyDescription}</p>
          ) : null}
        </ScenarioSection>
      ) : null}

      {/* 4. Contexte connu avant l'appel.
          ⚠️ Ne JAMAIS masquer cette section faute de champ dédié : le contexte existe
          toujours. Quand le cas n'en porte pas, c'est N8N qui en tire un au sort dans sa
          liste par simulateur — il n'est alors présent que dans le texte du scénario.
          Sans ce repli, l'apprenant perdrait sa situation de départ (régression introduite
          puis corrigée le 30/07). L'ordre de préférence :
            1. le brief joueur du cas (texte du client, il fait foi) ;
            2. le champ `priorContext` renvoyé par N8N ;
            3. à défaut, le récit complet — mieux vaut une redite que l'information perdue. */}
      {priorContext ? (
        <ScenarioSection icon="📋" title={labels.knownContext}>
          {renderBriefMarkdown(priorContext)}
        </ScenarioSection>
      ) : legacyFullText ? (
        <ScenarioSection icon="📋" title={labels.context}>
          {renderBriefMarkdown(legacyFullText)}
        </ScenarioSection>
      ) : null}

      {/* 5. Ce que le simulateur joué ajoute de propre à son exercice.
          Placée APRÈS le contexte — elle le complète, elle ne le remplace pas — et AVANT les
          critères, qui restent la dernière chose lue. Masquée quand le simulateur n'a rien de
          spécifique : un encadré vide vaut moins que pas d'encadré. */}
      {simulatorBrief ? (
        <ScenarioSection icon="📝" title={labels.simulatorBrief}>
          {renderBriefMarkdown(simulatorBrief)}
        </ScenarioSection>
      ) : null}

      {/* 6. Le niveau joué. Traduit ici et pas reçu de N8N : c'est notre enum.
          Décision Mariam du 15/10/2026 — le niveau seul, sans dire s'il a été imposé. */}
      {niveauJoue ? (
        <ScenarioSection icon="⚡" title={labels.difficulty}>
          <p className="text-sm font-medium text-slate-800">{niveauJoue}</p>
        </ScenarioSection>
      ) : null}

      {/* 7. Ce que ce cas pèse plus lourd. En dernier, juste avant « Appeler » : c'est la
          dernière chose lue, et ce sont les mots que le débrief reprendra. */}
      {focusCriteria.length > 0 ? (
        <ScenarioSection icon="🎯" title={labels.focusCriteria}>
          <div className="flex flex-wrap gap-1.5">
            {focusCriteria.map((c) => (
              <span
                key={c}
                className="text-xs font-medium rounded-full px-2.5 py-1"
                style={{ backgroundColor: "#988EF226", color: "#5A4BBE" }}
              >
                {c}
              </span>
            ))}
          </div>
        </ScenarioSection>
      ) : null}
    </>
  );
}
